/**
 * Invite User Form Schema
 *
 * Validation for sending (or editing) an invitation via `upsertInvitation`.
 * - `targetKind` is not entered by the user: the composer copies it from the selected
 *   invitation type so the schema can require a target unless the kind is `none`.
 * - Names are capped at 20 to match the user record created on accept.
 *
 * @module forms/users/inviteUser/schema
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

const formConfig = FormConfigLoader.loadFormConfig(formYaml)

export const FORM_CONFIG = formConfig as typeof formConfig & { editSubmitButton?: { label?: string } }
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

export const E164_MOBILE = /^\+[1-9][0-9]{7,14}$/

export const InviteUserFormSchema = z
  .object({
    invitationType: z.string().min(1, 'Choose what you are inviting them as'),
    targetKind: z.enum(['organization', 'none']).optional(),
    targetReference: z.string().max(500).optional(),
    firstName: z.string().trim().min(1, 'First name is required').max(20, 'First name cannot exceed 20 characters'),
    lastName: z.string().trim().min(1, 'Last name is required').max(20, 'Last name cannot exceed 20 characters'),
    emailId: z.string().trim().min(1, 'Email address is required').email('Invalid email address format').max(254),
    mobileNumber: z
      .string()
      .regex(E164_MOBILE, 'Use international format, e.g. +919876543210')
      .optional()
      .or(z.literal('')),
  })
  .superRefine((v, ctx) => {
    if (v.targetKind !== 'none' && !v.targetReference) {
      ctx.addIssue({ code: 'custom', path: ['targetReference'], message: 'Select an organization' })
    }
  })

export type InviteUserFormData = z.infer<typeof InviteUserFormSchema>

/**
 * Body for POST /uniq/invitations. Omit `invitationNumber` to create; include it to update.
 * `inviterUserNumber` is never sent — the backend takes it from the auth context.
 */
export function transformToApiRequest(data: InviteUserFormData, invitationNumber?: number) {
  return {
    ...(invitationNumber != null && { invitationNumber }),
    invitationType: data.invitationType,
    targetReference: data.targetKind === 'none' ? null : data.targetReference,
    firstName: data.firstName,
    lastName: data.lastName,
    emailId: data.emailId,
    mobileNumber: data.mobileNumber || null,
  }
}
